import { useMemo, useState, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { IconCalendar, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SearchableSelect, type SearchableSelectOption } from '../../components/ui/SearchableSelect';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useUrlFilterState } from '../../lib/url-filter-state';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { ReportAnalyticsGrid, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { currentMonthDateFilters, lastMonthDateFilters, todayDateFilters } from './report-filters';
import {
  fetchReportBillingDetails,
  fetchReportBills,
  fetchReportReceipts,
  fetchReportRefunds,
  reportSections,
} from './reports.api';
import { useBillingDetailsFilterOptions } from './reports.queries';
import type {
  BillingDetailRow,
  BillingDetailsFilterOptions,
  BillingDetailsFilters,
  BillingDetailSummary,
  BillReportSummary,
  PaginatedReport,
  ReceiptReportSummary,
  RefundReportSummary,
  ReportBillRow,
  ReportReceiptRow,
  ReportRefundRow,
  ReportSectionMetricSummary,
  ReportStatusMetricSummary,
} from './types';

export type BillingReportKind = 'bills' | 'receipts' | 'refunds' | 'billing-details';

type AnyReportRow = ReportBillRow | ReportReceiptRow | ReportRefundRow | BillingDetailRow;
type AnySummary = BillReportSummary | ReceiptReportSummary | RefundReportSummary | BillingDetailSummary;

interface BillingReportPageProps {
  kind: BillingReportKind;
}

interface Column<T> {
  key: string;
  label: string;
  sort?: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T, index: number) => ReactNode;
  exportValue: (row: T, index: number) => string | number | null;
  width?: number;
}

const reportTitles: Record<BillingReportKind, { title: string; description: string; empty: string }> = {
  bills: { title: 'All Bill', description: 'Bills raised across OPD, EMG, IPD, Daycare, Dialysis, and Investigation.', empty: 'No bills found' },
  receipts: { title: 'All Receipt', description: 'Receipt payments collected across all billing sections.', empty: 'No receipts found' },
  refunds: { title: 'All Refund', description: 'Refunds issued across all billing sections.', empty: 'No refunds found' },
  'billing-details': { title: 'Billing Report', description: 'Bill-wise payable, due, and credit with patient, doctor, and section information.', empty: 'No bills found' },
};

// Billing Report-only filters stay undefined on the other kinds, so their strict APIs never see them.
const initialFilters = (): BillingDetailsFilters => ({ page: 1, limit: 25, section: '', value: '', fromDate: '', toDate: '' });
const amount = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const statusLabel = (value: string | null | undefined) => value ? value.replace(/-/g, ' ') : '-';
const upper = (value: string | null | undefined) => value?.trim() ? value.trim().toUpperCase() : '-';
const doctorText = (value: string) => {
  const name = value.trim().replace(/^(dr\.?\s+)+/i, '');
  return name && name !== '-' ? `DR. ${name}` : '-';
};

const fetchReport = async (kind: BillingReportKind, filters: BillingDetailsFilters): Promise<PaginatedReport<AnyReportRow, AnySummary>> => {
  if (kind === 'bills') return fetchReportBills(filters);
  if (kind === 'receipts') return fetchReportReceipts(filters);
  if (kind === 'refunds') return fetchReportRefunds(filters);
  return fetchReportBillingDetails(filters);
};

const useBillingStyleReport = (kind: BillingReportKind, filters: BillingDetailsFilters) =>
  useQuery({ queryKey: ['reports', kind, filters], queryFn: () => fetchReport(kind, filters) });

const summaryValue = (summary: AnySummary | undefined, key: keyof BillReportSummary | keyof ReceiptReportSummary | keyof RefundReportSummary | keyof BillingDetailSummary) => {
  const value = summary && key in summary ? summary[key as keyof typeof summary] : undefined;
  return typeof value === 'number' ? value : 0;
};

const sectionMetrics = (summary: AnySummary | undefined): ReportSectionMetricSummary[] =>
  summary && 'bySection' in summary ? summary.bySection ?? [] : [];
const statusMetrics = (summary: AnySummary | undefined): ReportStatusMetricSummary[] =>
  summary && 'byStatus' in summary ? summary.byStatus ?? [] : [];
const modeMetrics = (summary: AnySummary | undefined): { mode: string; count: number; amount: number }[] =>
  summary && 'byMode' in summary ? summary.byMode ?? [] : [];
const departmentMetrics = (summary: AnySummary | undefined): { department: string; count: number; amount: number }[] =>
  summary && 'byDepartment' in summary ? summary.byDepartment ?? [] : [];

export const BillingReportPage = ({ kind }: BillingReportPageProps) => {
  const [filters, setFilters] = useUrlFilterState<BillingDetailsFilters>(() => ({
    ...initialFilters(),
    ...(kind === 'refunds' ? {} : kind === 'bills' ? lastMonthDateFilters() : currentMonthDateFilters()),
  }));
  const [draft, setDraft] = useState<BillingDetailsFilters>(filters);
  const query = useBillingStyleReport(kind, filters);
  const title = reportTitles[kind];
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...draft, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };
  const showToday = () => {
    const next = { ...draft, ...todayDateFilters() };
    setDraft(next);
    setFilters((current) => ({ ...next, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const changePage = (page: number) => setFilters((current) => ({ ...current, page }));
  const changeLimit = (limit: number) => {
    setDraft((current) => ({ ...current, limit }));
    setFilters((current) => ({ ...current, limit, page: 1 }));
  };
  const errorMessage = query.error instanceof ApiError ? query.error.message : `${title.title} could not be loaded.`;

  const columns = columnsFor(kind);
  const rows = query.data?.data ?? [];
  const sectionChartData = useMemo<ReportChartDatum[]>(() => sectionMetrics(query.data?.summary).map((item, index) => ({
    name: item.sectionLabel,
    Total: item.total ?? item.amount ?? 0,
    Amount: item.amount ?? item.total ?? 0,
    Payment: item.payment ?? 0,
    Due: item.due ?? 0,
    Refund: item.refund ?? 0,
    Discount: item.discount ?? 0,
    color: reportChartColor(index),
  })), [query.data?.summary]);
  const statusChartData = useMemo<ReportChartDatum[]>(() => statusMetrics(query.data?.summary).map((item, index) => ({
    name: statusLabel(item.status),
    value: item.total || item.count,
    color: reportChartColor(index),
  })), [query.data?.summary]);
  const modeChartData = useMemo<ReportChartDatum[]>(() => modeMetrics(query.data?.summary).map((item, index) => ({
    name: item.mode,
    value: item.amount,
    color: reportChartColor(index),
  })), [query.data?.summary]);
  const refundMixData = useMemo<ReportChartDatum[]>(() => sectionChartData.map((item, index) => ({
    name: item.name,
    value: typeof item.Amount === 'number' ? item.Amount : 0,
    color: reportChartColor(index),
  })), [sectionChartData]);
  const departmentChartData = useMemo<ReportChartDatum[]>(() => [...departmentMetrics(query.data?.summary)]
    .sort((left, right) => right.amount - left.amount)
    .slice(0, 8)
    .map((item) => ({ name: item.department, Amount: item.amount, Count: item.count })), [query.data?.summary]);

  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<AnyReportRow>((page, limit) => fetchReport(kind, { ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `${title.title.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`,
      title: title.title,
      subtitle: [filters.section ? reportSections.find((section) => section.value === filters.section)?.label : 'All sections', filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : null].filter(Boolean).join(' | '),
      columns: columns.map((column) => ({
        header: column.label,
        ...(column.width ? { width: column.width } : {}),
        align: column.align ?? 'left',
        map: (row: AnyReportRow, index: number) => column.exportValue(row, index),
      })),
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>{title.title}</h1>
          <p>{title.description}</p>
        </div>
      </div>

      {kind === 'billing-details' ? (
        <BillingDetailsFilterForm
          draft={draft}
          setDraft={setDraft}
          onSubmit={applyFilters}
          onToday={showToday}
          onReset={reset}
          onExport={exportToExcel}
          exportDisabled={!query.isSuccess}
        />
      ) : (
        <form className="report-list-filter-card" onSubmit={applyFilters}>
          <label>
            Section
            <select value={draft.section ?? ''} onChange={(event) => setDraft((current) => ({ ...current, section: event.target.value as BillingDetailsFilters['section'] }))}>
              {reportSections.map((section) => <option key={section.value || 'all'} value={section.value}>{section.label}</option>)}
            </select>
          </label>
          <label>
            From date
            <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
          </label>
          <label>
            To date
            <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
          </label>
          <label>
            Search
            <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Bill, patient, UHID, phone" />
          </label>
          <button className="button button-primary report-list-action" type="submit"><IconSearch />Search</button>
          <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
          <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
        </form>
      )}

      <section className="report-summary-grid">
        <SummaryTile label="Records" value={summaryValue(query.data?.summary, 'count')} />
        {kind === 'receipts' ? (
          <>
            <SummaryTile label="Cash" value={amount(summaryValue(query.data?.summary, 'cash'))} />
            <SummaryTile label="Bank" value={amount(summaryValue(query.data?.summary, 'bank'))} />
            <SummaryTile label="Total" value={amount(summaryValue(query.data?.summary, 'total'))} />
          </>
        ) : kind === 'billing-details' ? (
          <>
            <SummaryTile label="Payable" value={amount(summaryValue(query.data?.summary, 'payable'))} />
            <SummaryTile label="Discount" value={amount(summaryValue(query.data?.summary, 'discount'))} />
            <SummaryTile label="Due" value={amount(summaryValue(query.data?.summary, 'due'))} />
          </>
        ) : kind === 'refunds' ? (
          <SummaryTile label="Refund" value={amount(summaryValue(query.data?.summary, 'total'))} />
        ) : (
          <>
            <SummaryTile label="Total" value={amount(summaryValue(query.data?.summary, 'total'))} />
            <SummaryTile label="Payment" value={amount(summaryValue(query.data?.summary, 'payment'))} />
            <SummaryTile label="Due" value={amount(summaryValue(query.data?.summary, 'due'))} />
          </>
        )}
      </section>

      {query.isSuccess ? (
        <ReportAnalyticsGrid>
          <ReportBarChartCard
            eyebrow="Section analysis"
            title={kind === 'billing-details' ? 'Payable amount by section' : 'Amount by section'}
            data={sectionChartData}
            bars={kind === 'billing-details'
              ? [
                { key: 'Amount', name: 'Payable', color: '#0d6b66' },
                { key: 'Discount', name: 'Discount', color: '#c98a1c' },
              ]
              : kind === 'receipts' || kind === 'refunds'
                ? [{ key: 'Amount', name: 'Amount', color: '#0d6b66' }]
                : [
                  { key: 'Total', name: 'Total', color: '#0d6b66' },
                  { key: 'Payment', name: 'Payment', color: '#3777b7' },
                  { key: 'Due', name: 'Due', color: '#b1424d' },
                ]}
            valueKind="amount"
          />
          {kind === 'receipts' ? (
            <ReportDonutChartCard eyebrow="Receipt mix" title="Collection by payment mode" data={modeChartData} valueKind="amount" />
          ) : kind === 'billing-details' ? (
            <ReportBarChartCard
              eyebrow="Department analysis"
              title="Top departments by payable amount"
              data={departmentChartData}
              bars={[{ key: 'Amount', name: 'Payable', color: '#3777b7' }]}
              layout="vertical"
              height={Math.max(240, departmentChartData.length * 34 + 90)}
              valueKind="amount"
            />
          ) : kind === 'refunds' ? (
            <ReportDonutChartCard eyebrow="Refund mix" title="Refund amount by section" data={refundMixData} valueKind="amount" />
          ) : (
            <ReportDonutChartCard eyebrow="Status analysis" title="Bill status value" data={statusChartData} valueKind="amount" />
          )}
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title={`Unable to load ${title.title}`} message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title={title.empty} message="Change filters or date range and search again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              <table className={`report-data-table${kind === 'billing-details' ? ' billing-details-table' : ''}${kind === 'bills' ? ' all-bill-table' : ''}${kind === 'receipts' ? ' all-receipt-table' : ''}${kind === 'refunds' ? ' all-refund-table' : ''}`}>
                <thead>
                  <tr>
                    <th>{kind === 'billing-details' ? '#' : 'SN'}</th>
                    {columns.map((column) => column.sort
                      ? <SortableTh key={column.key} control={tableSort} column={column.sort}>{column.label}</SortableTh>
                      : <th key={column.key}>{column.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.id}
                      className={kind === 'bills' ? ((row as ReportBillRow).due > 0 ? 'all-bill-row-due' : 'all-bill-row-settled') : undefined}
                    >
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      {columns.map((column) => <td key={column.key} className={column.align === 'right' ? 'number' : undefined}>{column.render(row, index)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => changePage(filters.page - 1)}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => changePage(filters.page + 1)}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: string | number }) => (
  <div className="report-summary-tile">
    <span>{label}</span>
    <strong>{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</strong>
  </div>
);

// "All" is the empty value, shown as the dropdown's "Select" placeholder and restored with its clear button.
const sectionOptions: SearchableSelectOption[] = reportSections.filter((section) => section.value);
const doctorTypeOptions: { value: NonNullable<BillingDetailsFilters['doctorType']>; label: string }[] = [
  { value: 'Hospital-Doctor', label: 'Hospital Doctor' },
  { value: 'Outside-Doctor', label: 'Outside Doctor' },
  { value: 'Out-Doctor', label: 'Out Doctor' },
];
const paymentModeOptions: { value: NonNullable<BillingDetailsFilters['paymentMode']>; label: string }[] = [
  { value: 'Cash', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'Transfer to Bank Account', label: 'Transfer to Bank Account' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Online', label: 'Online' },
  { value: 'Other', label: 'Other' },
];
const discountOptions: { value: NonNullable<BillingDetailsFilters['discount']>; label: string }[] = [
  { value: 'with', label: 'With Discount' },
  { value: 'without', label: 'Without Discount' },
  { value: 'flat', label: 'Flat Discount' },
  { value: 'percentage', label: 'Percentage Discount' },
];

type TextFilterKey = 'user' | 'doctor' | 'charge' | 'chargeCategory' | 'chargeSubCategory' | 'referral' | 'marketBy' | 'provider';

const textFilters: Record<TextFilterKey, { label: string; options: keyof BillingDetailsFilterOptions }> = {
  user: { label: 'User', options: 'users' },
  doctor: { label: 'Doctor', options: 'doctors' },
  charge: { label: 'Charge', options: 'charges' },
  chargeCategory: { label: 'Charge Category', options: 'chargeCategories' },
  chargeSubCategory: { label: 'Charge Sub-Category', options: 'chargeSubCategories' },
  referral: { label: 'Referral', options: 'referrals' },
  marketBy: { label: 'Market By', options: 'marketBy' },
  provider: { label: 'Provider', options: 'providers' },
};

// Billing Report filter panel: three rows matching the legacy layout — master-data filters,
// payment / discount / referral filters, then the date range with the actions.
const BillingDetailsFilterForm = ({
  draft,
  setDraft,
  onSubmit,
  onToday,
  onReset,
  onExport,
  exportDisabled,
}: {
  draft: BillingDetailsFilters;
  setDraft: Dispatch<SetStateAction<BillingDetailsFilters>>;
  onSubmit: (event: FormEvent) => void;
  onToday: () => void;
  onReset: () => void;
  onExport: () => Promise<void>;
  exportDisabled: boolean;
}) => {
  const optionsQuery = useBillingDetailsFilterOptions();
  const nameOptions = useMemo(() => Object.fromEntries(
    (Object.entries(optionsQuery.data ?? {}) as [string, string[]][]).map(([key, names]) => [key, names.map((name) => ({ value: name, label: name }))]),
  ) as Partial<Record<keyof BillingDetailsFilterOptions, SearchableSelectOption[]>>, [optionsQuery.data]);
  const update = (patch: Partial<BillingDetailsFilters>) => setDraft((current) => ({ ...current, ...patch }));
  const selectField = (key: keyof BillingDetailsFilters, label: string, options: readonly SearchableSelectOption[]) => (
    <label key={key}>
      {label}
      <SearchableSelect
        options={options}
        value={String(draft[key] ?? '')}
        placeholder="Select"
        clearable
        onChange={(value) => update({ [key]: value })}
      />
    </label>
  );
  const textField = (key: TextFilterKey) => selectField(key, textFilters[key].label, nameOptions[textFilters[key].options] ?? []);

  return (
    <form className="report-list-filter-card billing-details-filters" onSubmit={onSubmit}>
      {selectField('section', 'Section', sectionOptions)}
      {textField('user')}
      {textField('doctor')}
      {selectField('doctorType', 'Doctor Type', doctorTypeOptions)}
      {textField('charge')}
      {textField('chargeCategory')}
      {textField('chargeSubCategory')}

      {selectField('paymentMode', 'Payment', paymentModeOptions)}
      {selectField('discount', 'Discount', discountOptions)}
      <label>
        Discount (Rs.)
        <input type="number" min={0} step="0.01" inputMode="decimal" value={draft.discountAmount ?? ''} placeholder="Exact amount" onChange={(event) => update({ discountAmount: event.target.value })} />
      </label>
      {textField('referral')}
      {textField('marketBy')}
      {textField('provider')}
      <label>
        Search
        <input value={draft.value ?? ''} onChange={(event) => update({ value: event.target.value })} placeholder="Bill, patient, UHID, phone" />
      </label>

      <label>
        From date
        <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => update({ fromDate: event.target.value })} />
      </label>
      <label>
        To date
        <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => update({ toDate: event.target.value })} />
      </label>
      <div className="billing-details-filter-actions">
        <button className="button button-primary report-list-action" type="submit"><IconSearch />Filter</button>
        <button className="button button-secondary report-list-action" type="button" onClick={onToday}><IconCalendar />Today</button>
        <button className="button button-secondary report-list-action" type="button" onClick={onReset}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="report-list-action" onExport={onExport} disabled={exportDisabled} label="Excel" />
      </div>
    </form>
  );
};

const SectionBadge = ({ row }: { row: { module: string; moduleLabel: string } }) => (
  <span className={`due-report-badge ${row.module}`}>{row.moduleLabel}</span>
);

const columnsFor = (kind: BillingReportKind): Column<AnyReportRow>[] => {
  if (kind === 'receipts') {
    return [
      {
        key: 'receiptNo', label: 'Receipt ID', sort: 'receiptNo', width: 18,
        render: (row) => {
          const receipt = row as ReportReceiptRow;
          return <><strong>{receipt.receiptNo}</strong><span className={`all-receipt-section ${receipt.module}`}>{receipt.moduleLabel}</span></>;
        },
        exportValue: (row) => (row as ReportReceiptRow).receiptNo,
      },
      {
        key: 'patient', label: 'Patient (UHID)', sort: 'patient', width: 28,
        render: (row) => {
          const receipt = row as ReportReceiptRow;
          const patient = upper(patientNameUhid(receipt));
          return receipt.patientId ? <Link className="patient-name-link" to={`/patients/${receipt.patientId}`} title="Open patient profile">{patient}</Link> : patient;
        },
        exportValue: (row) => upper(patientNameUhid(row as ReportReceiptRow)),
      },
      { key: 'phone', label: 'Phone', width: 16, render: (row) => (row as ReportReceiptRow).phone || '-', exportValue: (row) => (row as ReportReceiptRow).phone || '-' },
      { key: 'date', label: 'Date & Time', sort: 'date', width: 22, render: (row) => formatIpdDateTime((row as ReportReceiptRow).date), exportValue: (row) => formatIpdDateTime((row as ReportReceiptRow).date) },
      { key: 'amount', label: 'Amount (₹)', sort: 'amount', width: 14, render: (row) => amount((row as ReportReceiptRow).amount), exportValue: (row) => (row as ReportReceiptRow).amount },
      { key: 'mode', label: 'Receipt Mode', sort: 'mode', width: 24, render: (row) => (row as ReportReceiptRow).mode, exportValue: (row) => (row as ReportReceiptRow).mode },
      { key: 'bank', label: 'Bank', sort: 'bankName', width: 16, render: (row) => (row as ReportReceiptRow).bankName ?? '-', exportValue: (row) => (row as ReportReceiptRow).bankName ?? '-' },
      { key: 'receivedBy', label: 'Collected By', sort: 'receivedBy', width: 22, render: (row) => upper((row as ReportReceiptRow).receivedBy), exportValue: (row) => upper((row as ReportReceiptRow).receivedBy) },
    ];
  }
  if (kind === 'refunds') {
    return [
      { key: 'paymentId', label: 'Payment ID', sort: 'paymentId', width: 16, render: (row) => <strong>{(row as ReportRefundRow).paymentId}</strong>, exportValue: (row) => (row as ReportRefundRow).paymentId },
      {
        key: 'billId', label: 'Refund From Bill', sort: 'billId', width: 18,
        render: (row) => {
          const refund = row as ReportRefundRow;
          const href = billingDetailHref(refund);
          const billId = <strong>{refund.billId}</strong>;
          return href ? <Link className="bill-id-link" to={href} title="View bill details">{billId}</Link> : billId;
        },
        exportValue: (row) => (row as ReportRefundRow).billId,
      },
      { key: 'section', label: 'Section', sort: 'section', width: 16, render: (row) => upper((row as ReportRefundRow).moduleLabel), exportValue: (row) => upper((row as ReportRefundRow).moduleLabel) },
      {
        key: 'patient', label: 'Patient (UHID)', sort: 'patient', width: 28,
        render: (row) => {
          const refund = row as ReportRefundRow;
          const patient = upper(patientNameUhid(refund));
          return refund.patientId ? <Link className="patient-name-link" to={`/patients/${refund.patientId}`} title="Open patient profile">{patient}</Link> : patient;
        },
        exportValue: (row) => upper(patientNameUhid(row as ReportRefundRow)),
      },
      { key: 'phone', label: 'Phone', width: 16, render: (row) => (row as ReportRefundRow).phone || '-', exportValue: (row) => (row as ReportRefundRow).phone || '-' },
      { key: 'date', label: 'Date & Time', sort: 'date', width: 22, render: (row) => formatIpdDateTime((row as ReportRefundRow).date), exportValue: (row) => formatIpdDateTime((row as ReportRefundRow).date) },
      { key: 'amount', label: 'Amount (₹)', sort: 'amount', width: 14, render: (row) => amount((row as ReportRefundRow).amount), exportValue: (row) => (row as ReportRefundRow).amount },
      { key: 'refundedBy', label: 'Refund By', sort: 'refundedBy', width: 20, render: (row) => upper((row as ReportRefundRow).refundedBy), exportValue: (row) => upper((row as ReportRefundRow).refundedBy) },
    ];
  }
  if (kind === 'billing-details') {
    const detail = (row: AnyReportRow) => row as BillingDetailRow;
    return [
      {
        key: 'billId', label: 'Bill ID', sort: 'billId', width: 16,
        render: (row) => {
          const href = billingDetailHref(detail(row));
          const billId = <strong>{detail(row).billId}</strong>;
          return <>{href ? <Link className="bill-id-link" to={href} title="View bill details">{billId}</Link> : billId}<SectionBadge row={row} /></>;
        },
        exportValue: (row) => upper(detail(row).billId),
      },
      { key: 'billDate', label: 'Bill Date', sort: 'billDate', width: 20, render: (row) => formatIpdDateTime(detail(row).billDate), exportValue: (row) => formatIpdDateTime(detail(row).billDate) },
      {
        key: 'patient', label: 'Patient', sort: 'patient', width: 30,
        render: (row) => {
          const { patientId } = detail(row);
          return patientId ? <Link className="patient-name-link" to={`/patients/${patientId}`} title="Open patient profile">{patientNameUhid(detail(row))}</Link> : patientNameUhid(detail(row));
        },
        exportValue: (row) => upper(patientNameUhid(detail(row))),
      },
      { key: 'gender', label: 'Gender', sort: 'gender', width: 10, render: (row) => upper(detail(row).gender), exportValue: (row) => upper(detail(row).gender) },
      { key: 'age', label: 'Age', width: 14, render: (row) => detail(row).ageDisplay || '-', exportValue: (row) => detail(row).ageDisplay || '-' },
      { key: 'mobile', label: 'Mobile No', width: 14, render: (row) => detail(row).phone || '-', exportValue: (row) => detail(row).phone || '-' },
      { key: 'doctor', label: 'Doctor', sort: 'doctor', width: 24, render: (row) => doctorText(detail(row).doctorName), exportValue: (row) => upper(doctorText(detail(row).doctorName)) },
      { key: 'department', label: 'Department', sort: 'department', width: 18, render: (row) => detail(row).department, exportValue: (row) => upper(detail(row).department) },
      { key: 'payable', label: 'Payable', sort: 'payable', align: 'right', width: 14, render: (row) => amount(detail(row).payable), exportValue: (row) => detail(row).payable },
      {
        key: 'dueCredit', label: 'Due / Credit', sort: 'due', align: 'right', width: 22,
        render: (row) => {
          const { due, credit } = detail(row);
          if (due <= 0 && credit <= 0) return '-';
          return (
            <>
              {due > 0 ? <span className="billing-due-amount" title="Due">{amount(due)}</span> : null}
              {credit > 0 ? <span className="billing-credit-amount" title="Credit">{amount(credit)}</span> : null}
            </>
          );
        },
        exportValue: (row) => dueCreditText(detail(row)),
      },
      { key: 'createdBy', label: 'Created By', sort: 'createdBy', width: 22, render: (row) => detail(row).createdBy, exportValue: (row) => upper(detail(row).createdBy) },
    ];
  }
  return [
    {
      key: 'patient', label: 'Patient (UHID)', sort: 'patient', width: 30,
      render: (row) => {
        const bill = row as ReportBillRow;
        const identity = <strong>{upper(bill.patientName)}{bill.uhid ? ` (${bill.uhid})` : ''}</strong>;
        return bill.patientId ? <Link className="patient-name-link" to={`/patients/${bill.patientId}`} title="Open patient profile">{identity}</Link> : identity;
      },
      exportValue: (row) => patientNameUhid(row as ReportBillRow),
    },
    {
      key: 'genderAge', label: 'Gender & Age', width: 18,
      render: (row) => {
        const bill = row as ReportBillRow;
        return <span className="all-bill-gender-age"><span aria-hidden="true">⚥</span>{upper(bill.gender)}<IconCalendar />{bill.ageDisplay || '-'}</span>;
      },
      exportValue: (row) => {
        const bill = row as ReportBillRow;
        return `${upper(bill.gender)} | ${bill.ageDisplay || '-'}`;
      },
    },
    { key: 'phone', label: 'Phone', width: 16, render: (row) => (row as ReportBillRow).phone || '-', exportValue: (row) => (row as ReportBillRow).phone || '-' },
    {
      key: 'billId', label: 'Bill No', sort: 'billId', width: 16,
      render: (row) => {
        const bill = row as ReportBillRow;
        const href = billingDetailHref(bill);
        const billId = <strong>{bill.billId}</strong>;
        return href ? <Link className="bill-id-link" to={href} title="View bill details">{billId}</Link> : billId;
      },
      exportValue: (row) => (row as ReportBillRow).billId,
    },
    { key: 'section', label: 'Section', sort: 'section', width: 16, render: (row) => upper((row as ReportBillRow).moduleLabel), exportValue: (row) => upper((row as ReportBillRow).moduleLabel) },
    { key: 'billDate', label: 'Date & Time', sort: 'billDate', width: 22, render: (row) => formatIpdDateTime((row as ReportBillRow).billDate), exportValue: (row) => formatIpdDateTime((row as ReportBillRow).billDate) },
    {
      key: 'amount', label: 'Amount (₹)', sort: 'grandTotal', width: 20,
      render: (row) => {
        const bill = row as ReportBillRow;
        return (
          <div className="all-bill-amount">
            <span>Total : {billAmount(bill.grandTotal)}</span>
            {bill.due > 0 ? <span>Due : {billAmount(bill.due)}</span> : null}
            {bill.credit > 0 ? <span>Credit : {billAmount(bill.credit)}</span> : null}
          </div>
        );
      },
      exportValue: (row) => {
        const bill = row as ReportBillRow;
        return `Total: ${billAmount(bill.grandTotal)} | Due: ${billAmount(bill.due)} | Credit: ${billAmount(bill.credit)}`;
      },
    },
    { key: 'createdBy', label: 'Generated By', sort: 'createdBy', width: 24, render: (row) => upper((row as ReportBillRow).createdBy), exportValue: (row) => upper((row as ReportBillRow).createdBy) },
  ];
};

// Mirrors the Billing module's links: IPD / Daycare / Dialysis / OT bills open from their admission.
const billingDetailHref = (row: Pick<BillingDetailRow, 'module' | 'sourceId' | 'admissionId'>): string | null => {
  switch (row.module) {
    case 'opd': return `/opd/${row.sourceId}`;
    case 'emg': return `/emg/${row.sourceId}`;
    case 'investigation': return `/investigation-bill/${row.sourceId}`;
    case 'ipd':
    case 'daycare': return row.admissionId ? `/ipd-daycare/${row.admissionId}?tab=bill-details` : null;
    case 'dialysis': return row.admissionId ? `/dialysis/${row.admissionId}` : null;
    case 'ot': return row.admissionId ? `/ot/${row.admissionId}?tab=bill-details` : null;
  }
};

const dueCreditText = (row: Pick<BillingDetailRow, 'due' | 'credit'>) =>
  [row.due > 0 ? `DUE ${amount(row.due)}` : null, row.credit > 0 ? `CREDIT ${amount(row.credit)}` : null].filter(Boolean).join(' / ') || '-';

const patientNameUhid = (row: Pick<BillingDetailRow, 'patientName' | 'uhid'>) =>
  row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName;

const billAmount = (value: number) => value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
